import { initContract } from "@ts-rest/core";
import { StreamableFile } from "@nestjs/common";
import { z } from "zod";
import {
    addFieldReviewsBodySchema,
    transactionFieldReviewSchema,
    transactionReviewFieldEnum,
} from "@common/schemas/transactionFieldReview.schema";
import {
    getTransactionsQuerySchema,
    getTransactionsSnapshotQuerySchema,
    getTransactionsSnapshotResponseSchema,
    getTransactionsStatisticsQuerySchema,
    transactionsStatisticsResponseSchema,
    hasUnbilledCustomerExpensesQuerySchema,
    hasUnbilledCustomerExpensesResponseSchema,
    BulkUnsplitPayloadSchema,
    bulkFieldReviewRequestSchema,
    bulkUpdateTransactionsPartialFailureSchema,
    bulkUpdateTransactionsRequestSchema,
    bulkUpdateTransactionsResponseSchema,
    detachDocumentMatchResponseSchema,
    getTransactionsResponseSchema,
    getTransactionsResponseWithCountSchema,
    getSimilarTransactionsResponseSchema,
    linkFinancialTransactionDocumentBodySchema,
    matchTransferBodySchema,
    matchTransferResponseSchema,
    transactionExportQuerySchema,
    transactionMcpContextResponseSchema,
    unmatchTransferResponseSchema,
    updateTransactionSplitsV2BodySchema,
    uploadReceiptBodySchema,
    updateTransactionRequestSchema,
} from "@common/schemas/transaction.schema";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { TransactionDetailsResponseDto } from "@common/dto/transactions/transaction-details-response.dto";
import { UpdateTransactionResponseDto } from "@common/dto/transactions/update-transaction-response.dto";
import { FinancialDocumentExtendedDto } from "@common/dto/financialDocument/financial-document-extended.dto";
import {
    suggestedAccrualMatchBillsQuerySchema,
    suggestedAccrualMatchBillsResponseSchema,
    suggestedAccrualMatchInvoicesQuerySchema,
    suggestedAccrualMatchInvoicesResponseSchema,
} from "@common/schemas/domains/accrualReview/suggested-accrual-match-response.schema";

const c = initContract();
const TRANSACTION_PATH_PREFIX = { pathPrefix: "/transactions" } as const;

export const transactionCoreContract = c.router(
    {
        getTransactions: {
            method: "GET",
            path: "/",
            query: getTransactionsQuerySchema,
            responses: {
                200: getTransactionsResponseSchema,
            },
        },
        queryTransactions: {
            method: "POST",
            path: "/query",
            body: getTransactionsQuerySchema,
            responses: {
                200: getTransactionsResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        queryTransactionsSnapshot: {
            method: "POST",
            path: "/query-snapshot",
            body: getTransactionsSnapshotQuerySchema,
            responses: {
                200: getTransactionsSnapshotResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getTransaction: {
            method: "GET",
            path: "/:id",
            pathParams: z.object({
                id: z.coerce.number(),
            }),
            responses: {
                200: c.type<TransactionDetailsResponseDto>(),
                404: ErrorMessageSchema,
            },
        },
        getTransactionMcpContext: {
            method: "GET",
            path: "/:id/mcp-context",
            pathParams: z.object({
                id: z.coerce.number(),
            }),
            responses: {
                200: transactionMcpContextResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        getSimilarTransactions: {
            method: "GET",
            path: "/:id/similar",
            pathParams: z.object({
                id: z.coerce.number(),
            }),
            responses: {
                200: getSimilarTransactionsResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        updateTransaction: {
            method: "PATCH",
            path: "/:id",
            pathParams: z.object({
                id: z.coerce.number(),
            }),
            body: updateTransactionRequestSchema,
            responses: {
                200: c.type<UpdateTransactionResponseDto>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        matchTransfer: {
            method: "POST",
            path: "/:originTransactionId/match-transfer",
            pathParams: z.object({
                originTransactionId: z.coerce.number(),
            }),
            body: matchTransferBodySchema,
            responses: {
                200: matchTransferResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        unmatchTransfer: {
            method: "DELETE",
            path: "/:financialTransactionId/match-transfer",
            pathParams: z.object({
                financialTransactionId: z.coerce.number(),
            }),
            responses: {
                200: unmatchTransferResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        linkDocument: {
            method: "POST",
            path: "/link",
            body: linkFinancialTransactionDocumentBodySchema,
            responses: {
                200: getTransactionsResponseSchema.shape.data.element,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        detachDocumentMatch: {
            method: "POST",
            path: "/:financialTransactionId/detach/:matchId",
            pathParams: z.object({
                financialTransactionId: z.coerce.number(),
                matchId: z.coerce.number(),
            }),
            body: null,
            responses: {
                200: detachDocumentMatchResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateTransactionSplitsV2: {
            method: "PUT",
            path: "/:id/splits/v2",
            pathParams: z.object({
                id: z.coerce.number(),
            }),
            body: updateTransactionSplitsV2BodySchema,
            responses: {
                200: getTransactionsResponseSchema.shape.data.element.partial(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    TRANSACTION_PATH_PREFIX,
);

export const transactionAuxContract = c.router(
    {
        uploadReceipt: {
            method: "POST",
            path: "/:id/receipt",
            pathParams: z.object({
                id: z.coerce.number(),
            }),
            body: uploadReceiptBodySchema,
            responses: {
                200: c.type<FinancialDocumentExtendedDto & { id: number }>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getTransactionsStatistics: {
            method: "GET",
            path: "/statistics",
            query: getTransactionsStatisticsQuerySchema,
            responses: {
                200: transactionsStatisticsResponseSchema,
            },
        },
        queryTransactionsStatistics: {
            method: "POST",
            path: "/statistics/query",
            body: getTransactionsStatisticsQuerySchema,
            responses: {
                200: transactionsStatisticsResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        hasUnbilledCustomerExpenses: {
            method: "GET",
            path: "/has-unbilled-customer-expenses",
            query: hasUnbilledCustomerExpensesQuerySchema,
            responses: {
                200: hasUnbilledCustomerExpensesResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        bulkUnsplitTransactions: {
            method: "POST",
            path: "/:workspaceId/bulk-unsplit",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: BulkUnsplitPayloadSchema,
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
            },
        },
        bulkUpdateTransactions: {
            method: "POST",
            path: "/:workspaceId/bulk-update",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: bulkUpdateTransactionsRequestSchema,
            responses: {
                201: bulkUpdateTransactionsResponseSchema,
                400: z.union([
                    bulkUpdateTransactionsPartialFailureSchema,
                    ErrorMessageSchema,
                ]),
            },
        },
        getSuggestedMatches: {
            method: "GET",
            path: "/:id/suggested-matches",
            pathParams: z.object({
                id: z.coerce.number(),
            }),
            query: getTransactionsQuerySchema.pick({
                sort: true,
                filters: true,
            }),
            responses: {
                200: getTransactionsResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        getSuggestedAccrualMatchTransactions: {
            method: "GET",
            path: "/:id/suggested-accrual-match-transactions",
            pathParams: z.object({
                id: z.coerce.number(),
            }),
            query: getTransactionsQuerySchema.pick({
                sort: true,
                filters: true,
            }),
            responses: {
                200: getTransactionsResponseWithCountSchema,
                404: ErrorMessageSchema,
            },
        },
        getSuggestedAccrualMatchInvoices: {
            method: "GET",
            path: "/:id/suggested-accrual-match-invoices",
            pathParams: z.object({
                id: z.coerce.number(),
            }),
            query: suggestedAccrualMatchInvoicesQuerySchema,
            responses: {
                200: suggestedAccrualMatchInvoicesResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        getSuggestedAccrualMatchBills: {
            method: "GET",
            path: "/:id/suggested-accrual-match-bills",
            pathParams: z.object({
                id: z.coerce.number(),
            }),
            query: suggestedAccrualMatchBillsQuerySchema,
            responses: {
                200: suggestedAccrualMatchBillsResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        exportTransactionsCsv: {
            method: "GET",
            path: "/:workspaceId/csv",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: transactionExportQuerySchema,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportTransactionsXlsx: {
            method: "GET",
            path: "/:workspaceId/xlsx",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: transactionExportQuerySchema,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    TRANSACTION_PATH_PREFIX,
);

export type TransactionContract = typeof transactionCoreContract &
    typeof transactionAuxContract;

export const transactionContract: TransactionContract = {
    ...transactionCoreContract,
    ...transactionAuxContract,
};

export const transactionFieldReviewContract = c.router(
    {
        addFieldReviews: {
            method: "POST",
            path: "/:transactionId/field-reviews",
            pathParams: z.object({
                transactionId: z.coerce.number(),
            }),
            body: addFieldReviewsBodySchema,
            responses: {
                200: z.object({
                    reviews: z.array(transactionFieldReviewSchema),
                }),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        revokeFieldReview: {
            method: "DELETE",
            path: "/:transactionId/field-reviews/:field",
            pathParams: z.object({
                transactionId: z.coerce.number(),
                field: transactionReviewFieldEnum,
            }),
            body: c.noBody(),
            responses: {
                200: z.object({ deleted: z.boolean() }),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        bulkAddFieldReviews: {
            method: "POST",
            path: "/bulk-field-reviews",
            body: bulkFieldReviewRequestSchema,
            responses: {
                200: z.object({ reviewsCreated: z.number() }),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
            },
        },
        bulkRevokeFieldReviews: {
            method: "DELETE",
            path: "/bulk-field-reviews",
            body: bulkFieldReviewRequestSchema,
            responses: {
                200: z.object({ reviewsDeleted: z.number() }),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/transactions",
    },
);
